import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { db } from '../firebaseconfig';
import '../css/Profit.css';
import BudgetUsageBarChart from '../modules/profit/BudgetUsageBarChart';
import BudgetSummary from '../modules/Control/BudgetSummary';
import AreaBudgetPieChart from '../modules/profit/AreaBudgetPieChart';

export default function ProfitPage() {
  const [budgets, setBudgets] = useState({});
  const [expandedArea, setExpandedArea] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchBudgets();
  }, []);

  const fetchBudgets = async () => {
    try {
      const budgetsCollection = collection(db, 'budgets');
      const querySnapshot = await getDocs(budgetsCollection);
      const budgetsData = {};
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        const id = doc.id.trim().toLowerCase();
        if (data.type === 'area') {
          budgetsData[id] = {
            id: id,
            name: doc.id,
            amount: data.amount || 0,
            used: data.used || 0,
            usedarea: data.usedarea || 0,
            type: data.type,
            subAreas: {}
          };
        }
      });

      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        const id = doc.id.trim().toLowerCase();
        if (data.type === 'subarea' && data.from) {
          const mainArea = data.from.trim().toLowerCase();
          const foundMainArea = Object.keys(budgetsData).find(
            area => area.toLowerCase() === mainArea
          );
          if (foundMainArea) {
            budgetsData[foundMainArea].subAreas[id] = {
              id: id,
              name: doc.id,
              amount: data.amount || 0,
              used: data.used || 0,
              type: data.type,
              from: data.from
            };
          } else {
            console.warn(`Main area not found for subarea: ${doc.id}`);
          }
        }
      });

      Object.keys(budgetsData).forEach(areaId => {
        const areaData = budgetsData[areaId];
        areaData.remaining = areaData.amount - areaData.used;
      });

      console.log('Budget data loaded:', budgetsData);
      setBudgets(budgetsData);
    } catch (error) {
      console.error('Error fetching budgets:', error);
      Swal.fire('Error', 'Error al cargar los presupuestos', 'error');
    }
  };

  const handleToggleExpand = (area, e) => {
    e.stopPropagation();
    setExpandedArea(prevArea => prevArea === area ? null : area);
  };

  const handleBudgetClick = (area, subArea) => {
    navigate('/admin/control', { state: { selectedArea: area, specificArea: subArea || area } });
  };

  const handleEdit = (area, subArea, e) => {
    e.stopPropagation();
    const id = subArea ? `${area}-${subArea}` : area;
    setEditingId(prevId => prevId === id ? null : id);
    const amount = subArea ? budgets[area].subAreas[subArea].amount : budgets[area].amount;
    setEditAmount(amount.toString());
  };

  const handleSave = async (area, subArea, e) => {
    e.stopPropagation();
    try {
      const newAmount = parseFloat(editAmount);
      
      if (subArea) {
        const mainAreaBudget = budgets[area].amount;
        const currentSubAreaTotal = Object.values(budgets[area].subAreas)
          .reduce((sum, subArea) => sum + subArea.amount, 0) - budgets[area].subAreas[subArea].amount;
        
        if (currentSubAreaTotal + newAmount > mainAreaBudget) {
          Swal.fire('Error', 'El presupuesto de la subárea no puede superar el presupuesto del área principal.', 'error');
          return;
        }
      } else {
        const subAreasTotal = Object.values(budgets[area].subAreas)
          .reduce((sum, subArea) => sum + subArea.amount, 0);
        
        if (newAmount < subAreasTotal) {
          Swal.fire('Error', 'El presupuesto del área principal no puede ser menor que la suma de los presupuestos de las subáreas.', 'error');
          return;
        }
      }

      const budgetsCollection = collection(db, 'budgets');
      const querySnapshot = await getDocs(budgetsCollection);
      const docName = querySnapshot.docs.find(doc => 
        doc.id.toLowerCase() === (subArea || area).toLowerCase()
      )?.id;

      if (!docName) {
        throw new Error(`No se encontró el documento para ${subArea || area}`);
      }

      const budgetRef = doc(db, 'budgets', docName);
      await updateDoc(budgetRef, { amount: newAmount });
      
      setBudgets(prev => {
        const newBudgets = { ...prev };
        if (subArea) {
          newBudgets[area].subAreas[subArea].amount = newAmount;
        } else {
          newBudgets[area].amount = newAmount;
        }
        newBudgets[area].remaining = newBudgets[area].amount - newBudgets[area].used;
        return newBudgets;
      });
      setEditingId(null);
      
      Swal.fire('Éxito', 'Presupuesto actualizado con éxito', 'success');
    } catch (error) {
      console.error('Error updating budget:', error);
      Swal.fire('Error', 'Error al actualizar el presupuesto: ' + error.message, 'error');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(amount);
  };

  const renderBudgetCard = (budget, area, subArea = null) => {
    const id = subArea ? `${area}-${subArea}` : area;
    const isMainArea = !subArea;
    const isEditing = editingId === id;
    const isExpanded = expandedArea === area;

    let budgetData;
    if (isMainArea) {
      budgetData = {
        totalCost: budget.used,
        budget: budget.amount,
        remaining: budget.remaining,
        isExceeded: budget.remaining < 0
      };
    } else {
      budgetData = {
        totalCost: budget.used,
        budget: budget.amount,
        remaining: budget.amount - budget.used,
        isExceeded: (budget.amount - budget.used) < 0
      };
    }

    return (
      <div key={id} className={`budget-card ${isMainArea ? 'main-area' : 'sub-area'}`}>
        <div className={`card ${isEditing ? 'is-flipped' : ''}`}>
          <div className="card__side card__side--front">
            <div className="card-header">
              <h3>{budget.name}</h3>
            </div>
            <div className="card-content">
              <BudgetSummary budgetData={budgetData} formatCurrency={formatCurrency} />
              <div className="button-group">
                {isMainArea && (
                  <button onClick={(e) => handleToggleExpand(area, e)} className="btn btn-expand">
                    {isExpanded ? 'Contraer' : 'Expandir'}
                  </button>
                )}
                <button onClick={(e) => handleBudgetClick(area, subArea)} className="btn btn-details">
                  Ver detalles
                </button>
                <button onClick={(e) => handleEdit(area, subArea, e)} className="btn btn-edit">
                  Editar
                </button>
              </div>
            </div>
          </div>
          <div className="card__side card__side--back">
            <div className="card-header">
              <h3>Editar Presupuesto</h3>
            </div>
            <div className="card-content">
              <div className="edit-form">
                <input
                  type="number"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  className="edit-input"
                />
                <div className="button-group">
                  <button onClick={(e) => handleSave(area, subArea, e)} className="btn btn-save">
                    Guardar
                  </button>
                  <button onClick={() => setEditingId(null)} className="btn btn-cancel">
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="profit-page">
      <h1 className="profit-page__title">Panel de Presupuestos</h1>
      <div className="profit-page__budget-charts-panel">
        <div className="profit-page__area-charts-grid">
          <AreaBudgetPieChart
            budgets={budgets}
            formatCurrency={formatCurrency}
            handleBudgetClick={handleBudgetClick}
          />
        </div>
      </div>
      <div className="profit-page__budget-grid">
        {Object.entries(budgets).map(([area, areaData]) => (
          <div key={area} className={`profit-page__area-container ${expandedArea === area ? 'profit-page__area-container--expanded' : ''}`}>
            {renderBudgetCard(areaData, area)}
            <div className={`profit-page__sub-areas ${expandedArea === area ? 'expanded' : ''}`}>
              {Object.entries(areaData.subAreas).map(([subArea, subAreaData]) => 
                renderBudgetCard(subAreaData, area, subArea)
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

